// Rotating copy for the patient welcome board. The display advances through
// these on a timer (every few minutes) with a soft cross-fade so the screen is
// never static — keeps the message fresh and helps avoid kiosk burn-in.
//
// Bilingual: each list holds parallel `th` and `en` arrays where `th[i]` and
// `en[i]` are the SAME message in both languages (same length). The display
// keeps one rotation index and renders the array for its active language, so
// switching language shows the current line translated rather than jumping.
//
// These are a curated long-list to choose from; trim/reorder freely (keep th/en
// aligned). Index 0 of each Thai list matches the original hardcoded copy so the
// default screen looks unchanged on first paint.

// "เคล็ดลับรอยยิ้มวันนี้" — short, warm care tips.
export const CARE_TIPS = {
  th: [
    "ผ่อนคลายขากรรไกรจะช่วยให้การรักษาง่ายขึ้น ปล่อยไหล่ให้สบายและหายใจช้าๆ คุณอยู่ในการดูแลที่ดีที่สุดแล้วค่ะ",
    "แปรงฟันเบาๆ เป็นวงกลมอย่างน้อยวันละสองครั้ง ช่วยถนอมเหงือกและเคลือบฟันให้แข็งแรง",
    "ดื่มน้ำเปล่าหลังมื้ออาหารช่วยลดคราบและกรด ทำให้รอยยิ้มสดใสได้ทั้งวัน",
    "ใช้ไหมขัดฟันวันละครั้งก่อนนอน เข้าถึงซอกฟันที่แปรงไปไม่ถึงได้อย่างอ่อนโยน",
    "พบทันตแพทย์เพื่อตรวจและขูดหินปูนทุก 6 เดือน ดูแลก่อนปัญหาจะลุกลาม",
    "ลดของหวานและเครื่องดื่มมีน้ำตาล แล้วยิ้มของคุณจะขอบคุณคุณในระยะยาวค่ะ",
    "เปลี่ยนแปรงสีฟันทุก 3 เดือน หรือเมื่อขนแปรงเริ่มบาน เพื่อการทำความสะอาดที่ดีที่สุด",
    "แปรงลิ้นเบาๆ ทุกครั้งหลังแปรงฟัน ช่วยลดแบคทีเรียและทำให้ลมหายใจสดชื่น",
    "เลือกแปรงสีฟันขนนุ่ม ถนอมเหงือกและไม่ทำให้เคลือบฟันสึก",
    "ยาสีฟันที่มีฟลูออไรด์ช่วยเสริมความแข็งแรงของฟันและป้องกันฟันผุได้ดี",
    "หลังแปรงฟันด้วยยาสีฟันฟลูออไรด์ บ้วนน้ำแต่น้อย เพื่อให้ฟลูออไรด์ได้ปกป้องฟันเต็มที่",
    "เคี้ยวอาหารให้ทั่วทั้งสองข้าง ช่วยให้ฟันและกล้ามเนื้อขากรรไกรทำงานสมดุล",
    "หลีกเลี่ยงการใช้ฟันกัดของแข็ง เช่น น้ำแข็งหรือฝาขวด เพื่อป้องกันฟันร้าวหรือบิ่น",
    "เคี้ยวหมากฝรั่งไร้น้ำตาลหลังมื้ออาหารช่วยกระตุ้นน้ำลายและลดกรดในช่องปาก",
    "หากนอนกัดฟัน ลองปรึกษาทันตแพทย์เรื่องเฝือกสบฟัน เพื่อถนอมฟันยามหลับ",
    "ผักและผลไม้กรอบ เช่น แอปเปิ้ลและแครอท ช่วยขัดผิวฟันตามธรรมชาติ",
    "งดสูบบุหรี่ช่วยลดคราบ กลิ่นปาก และความเสี่ยงของโรคเหงือกได้มาก",
    "หากเสียวฟันเมื่อทานของร้อนหรือเย็น ลองใช้ยาสีฟันสำหรับฟันเสียว และแจ้งทันตแพทย์",
    "พาเด็กๆ มาพบทันตแพทย์ตั้งแต่ฟันซี่แรกขึ้น สร้างนิสัยรักสุขภาพช่องปากแต่เนิ่นๆ",
    "ดูแลเหงือกให้แข็งแรงด้วยการแปรงและใช้ไหมขัดฟันสม่ำเสมอ เพราะเหงือกที่ดีคือรากฐานของฟันที่ดี",
  ],
  en: [
    "Relaxing your jaw makes treatment easier. Let your shoulders ease and breathe slowly — you're in the very best of hands.",
    "Brush gently in small circles at least twice a day to protect your gums and keep your enamel strong.",
    "Drinking water after meals rinses away plaque and acid, keeping your smile fresh all day.",
    "Floss once a day before bed to gently clean between teeth where a brush can't reach.",
    "See your dentist for a check-up and scaling every 6 months to catch problems early.",
    "Cut back on sweets and sugary drinks — your smile will thank you in the long run.",
    "Replace your toothbrush every 3 months, or whenever the bristles fray, for the best clean.",
    "Gently brush your tongue after brushing your teeth to reduce bacteria and freshen your breath.",
    "Choose a soft-bristled toothbrush to protect your gums and avoid wearing down enamel.",
    "Fluoride toothpaste strengthens your teeth and helps protect against cavities.",
    "After brushing with fluoride toothpaste, rinse only a little so the fluoride can keep protecting your teeth.",
    "Chew on both sides of your mouth to keep your teeth and jaw muscles balanced.",
    "Avoid biting hard objects like ice or bottle caps to prevent cracked or chipped teeth.",
    "Chewing sugar-free gum after meals stimulates saliva and reduces acid in the mouth.",
    "If you grind your teeth at night, ask your dentist about a night guard to protect them while you sleep.",
    "Crunchy fruits and vegetables like apples and carrots help clean your teeth naturally.",
    "Quitting smoking greatly reduces staining, bad breath, and the risk of gum disease.",
    "If hot or cold foods make your teeth sensitive, try a toothpaste for sensitive teeth and tell your dentist.",
    "Bring children to the dentist as soon as their first tooth appears to build healthy habits early.",
    "Keep your gums healthy with regular brushing and flossing — healthy gums are the foundation of healthy teeth.",
  ],
};

// Reassuring welcome-copy paragraphs shown under the greeting.
export const WELCOME_LINES = {
  th: [
    "เชิญนั่งพักผ่อนนะคะ ทีมงานของเราพร้อมดูแลรอยยิ้มของคุณในวันนี้แล้ว หากต้องการสิ่งใด บอกเราได้เลยค่ะ",
    "ขอบคุณที่ไว้วางใจให้เราดูแลค่ะ เราจะดูแลคุณอย่างอ่อนโยนและใส่ใจในทุกขั้นตอน",
    "หากรู้สึกกังวลหรือไม่สบายตรงไหน บอกทีมงานได้ทันที เราพร้อมอยู่เคียงข้างคุณเสมอค่ะ",
    "สูดหายใจเข้าลึกๆ แล้วผ่อนคลายนะคะ วันนี้คุณอยู่ในมือของทีมที่พร้อมดูแลอย่างเต็มที่",
    "รอยยิ้มที่ดีเริ่มต้นจากการดูแลที่อบอุ่น ยินดีที่ได้เป็นส่วนหนึ่งในการดูแลคุณวันนี้ค่ะ",
    "ยินดีต้อนรับสู่คลินิกของเราค่ะ ขอให้คุณรู้สึกเหมือนอยู่บ้าน และวางใจได้ในทุกการดูแล",
    "นั่งพักให้สบายนะคะ อีกสักครู่ทีมทันตแพทย์จะมาดูแลคุณด้วยความตั้งใจอย่างเต็มที่",
    "เราเชื่อว่าการดูแลที่ดีเริ่มจากการรับฟัง หากมีคำถามหรือกังวลใจ เราพร้อมรับฟังเสมอค่ะ",
    "ขอบคุณที่ให้เราเป็นส่วนหนึ่งในการดูแลสุขภาพช่องปากของคุณ เราจะดูแลอย่างสุดความสามารถค่ะ",
    "ความสบายใจของคุณคือสิ่งสำคัญที่สุดสำหรับเรา หากต้องการพักหรือปรับท่าทาง บอกได้เลยนะคะ",
    "วันนี้เป็นอีกก้าวของการดูแลรอยยิ้มให้แข็งแรง ยินดีที่ได้เดินไปพร้อมกับคุณค่ะ",
    "ทีมของเราพร้อมดูแลคุณด้วยความนุ่มนวลและเข้าใจ ขอให้คุณรู้สึกผ่อนคลายตลอดการรักษา",
    "ไม่ว่าจะเป็นการตรวจเล็กๆ หรือการรักษาที่ซับซ้อน เราจะดูแลคุณอย่างอ่อนโยนในทุกขั้นตอน",
    "ยินดีต้อนรับค่ะ ขอให้เวลาที่อยู่กับเราเป็นช่วงเวลาที่สบายใจและไว้วางใจได้",
    "เราดูแลทุกคนในครอบครัวเหมือนคนในครอบครัวของเราเอง ขอบคุณที่เลือกให้เราดูแลคุณค่ะ",
    "หากรู้สึกตื่นเต้นหรือกังวล เป็นเรื่องปกตินะคะ เราจะค่อยๆ ดูแลคุณไปทีละขั้นอย่างใจเย็น",
    "สุขภาพช่องปากที่ดีคือของขวัญระยะยาวให้ตัวเอง ยินดีที่ได้ช่วยดูแลรอยยิ้มของคุณวันนี้ค่ะ",
    "ขอบคุณที่มาดูแลรอยยิ้มของคุณในวันนี้ เราพร้อมมอบการดูแลที่ใส่ใจและอบอุ่นให้คุณค่ะ",
  ],
  en: [
    "Please make yourself comfortable. Our team is ready to care for your smile today — just let us know if you need anything.",
    "Thank you for trusting us with your care. We'll look after you gently and attentively every step of the way.",
    "If you feel anxious or uncomfortable at any point, tell our team right away — we're always here beside you.",
    "Take a deep breath and relax. Today you're in the hands of a team ready to care for you fully.",
    "A great smile begins with warm care. We're delighted to be part of looking after you today.",
    "Welcome to our clinic. We hope you feel at home and at ease with every part of your care.",
    "Please relax and settle in. Our dental team will be with you shortly to care for you wholeheartedly.",
    "We believe good care begins with listening. If you have any questions or concerns, we're always ready to hear them.",
    "Thank you for letting us be part of caring for your oral health. We'll do our very best for you.",
    "Your comfort means everything to us. If you'd like to take a break or adjust your position, just say the word.",
    "Today is another step toward a stronger, healthier smile. We're glad to walk it with you.",
    "Our team is here to care for you with gentleness and understanding. We hope you feel relaxed throughout your treatment.",
    "Whether it's a simple check-up or a complex treatment, we'll care for you gently every step of the way.",
    "Welcome. We hope your time with us feels comfortable and reassuring.",
    "We care for every family as if they were our own. Thank you for choosing us to look after you.",
    "Feeling nervous or anxious is completely normal. We'll guide you calmly, one gentle step at a time.",
    "Good oral health is a lasting gift to yourself. We're happy to help care for your smile today.",
    "Thank you for coming in to care for your smile today. We're ready to give you warm, attentive care.",
  ],
};
